// src/pages/lms/LMSEvents.jsx

import { useState, useEffect } from 'react';

import { supabase } from '../../lib/supabase';

import { useLMSAuth } from '../../hooks/useLMSAuth';

import LMSLayout from './LMSLayout';

import { Plus, X, CalendarDays, Trash2, Send } from 'lucide-react';
import { notifyEventCreated } from '../../services/NotificationService';

const CATEGORIES = [
  'sports',
  'academic',
  'cultural',
  'poetry',
  'music',
  'tech',
  'health',
  'social',
  'workshop',
  'seminar',
  'art',
  'debate',
  'film',
  'religious',
  'general',
];

const EMOJIS = [
  '🎉',
  '🏆',
  '📚',
  '🎭',
  '🎵',
  '💻',
  '🏃',
  '🎨',
  '🤝',
  '🎓',
  '⚽',
  '🎤',
  '🏥',
  '🌟',
  '📅',
];

export default function LMSEvents() {
  const { account, institution } = useLMSAuth();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'general',
    avatar_emoji: '🎉',
    event_date: '',
    venue: '',
    organizer: '',
    priority: 'normal',
    target_degree: 'all',
  });

  useEffect(() => {
    if (institution?.id) load();
  }, [institution?.id]);

  const load = async () => {
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('lms_events')
        .select('*')
        .eq('institution_id', institution.id)
        .eq('is_published', true)
        .order('event_date', { ascending: true });


      if (error) throw error;

      setEvents(data || []);
    } catch (e) {
      console.error('Events load failed:', e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.event_date) return;

    setSaving(true);

    try {
      const eventDate = new Date(form.event_date).toISOString();

      const { data: ev, error: eventError } = await supabase
        .from('lms_events')
        .insert({
          institution_id: institution.id,
          institution_account_id: account.id,
          title: form.title.trim(),
          description: form.description.trim(),
          category: form.category,
          avatar_emoji: form.avatar_emoji,
          event_date: eventDate,
          venue: form.venue.trim(),
          organizer: form.organizer.trim() || institution.name,
          priority: form.priority,
          target_degree: form.target_degree,
          is_published: true,
          synced_to_app: false,
        })
        .select()
        .single();

      if (eventError) throw eventError;

      // Also post to the main app events table.
      if (ev && account.auth_user_id) {
        const { data: appEv } = await supabase.from('events').insert({
          created_by:             account.auth_user_id,
          creator_name:           institution.name,
          creator_role:           'institution',
          institution_id:         institution.id,
          title:                  form.title.trim(),
          description:            form.description.trim(),
          category:               form.category,
          avatar_emoji:            form.avatar_emoji,
          event_date:              new Date(form.event_date).toISOString(),
          venue:                   form.venue.trim(),
          organizer:               form.organizer.trim() || institution.name,
          priority:                form.priority,
          posted_by_institution:  true,
          institution_account_id: account.id,
        }).select().single();

        if (appEv) {
          await supabase.from('lms_events').update({
            synced_to_app: true,
            app_event_id: appEv.id,
          }).eq('id', ev.id);
        }
      }

      // Notify students after the event has been created successfully.
      if (ev) {
        try {
          await notifyEventCreated(institution.id, ev);
        } catch (notificationError) {
          console.error('Event notification failed:', notificationError);
        }
      }

      await load();

      setShowForm(false);

      setForm({
        title: '',
        description: '',
        category: 'general',
        avatar_emoji: '🎉',
        event_date: '',
        venue: '',
        organizer: '',
        priority: 'normal',
        target_degree: 'all',
      });
    } catch (e) {
      console.error('Event creation failed:', e);
      alert(e.message || 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id, appEventId) => {
    try {
      const { error } = await supabase
        .from('lms_events')
        .update({
          is_published: false,
        })
        .eq('id', id);

      if (error) throw error;

      if (appEventId) {
        const { error: appError } = await supabase
          .from('events')
          .update({
            is_cancelled: true,
          })
          .eq('id', appEventId);

        if (appError) throw appError;
      }

      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      console.error('Event deletion failed:', e);
      alert(e.message || 'Failed to delete event');
    }
  };

  const priorityColor = p =>
    ({
      normal: '#10B981',
      important: '#F59E0B',
      very_important: '#EF4444',
    })[p] || '#10B981';

  const inp = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1.5px solid #E5E7EB',
    fontSize: 13,
    boxSizing: 'border-box',
    outline: 'none',
    color: '#111827',
  };

  return (
    <LMSLayout>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: '#111827',
              margin: 0,
            }}
          >
            Events
          </h1>

          <p
            style={{
              color: '#6B7280',
              fontSize: 13,
              margin: '4px 0 0',
            }}
          >
            Events posted here appear on the student app events screen
          </p>
        </div>

        <button
          onClick={() => setShowForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg,#10B981,#059669)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          Create Event
        </button>
      </div>

      <div
        style={{
          background: '#F0FDF4',
          border: '1px solid #BBF7D0',
          borderRadius: 12,
          padding: '12px 16px',
          marginBottom: 20,
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <CalendarDays
          size={16}
          color="#10B981"
          style={{
            marginTop: 1,
            flexShrink: 0,
          }}
        />

        <div
          style={{
            fontSize: 13,
            color: '#065F46',
            lineHeight: 1.5,
          }}
        >
          Events are automatically synced to the{' '}
          <strong>UNIVERSE app Events screen</strong> and are visible only to
          students of your institution.
        </div>
      </div>

      {loading ? (
        <div
          style={{
            textAlign: 'center',
            padding: 48,
            color: '#9CA3AF',
          }}
        >
          Loading events...
        </div>
      ) : events.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 48,
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #E5E7EB',
          }}
        >
          <CalendarDays
            size={40}
            color="#E5E7EB"
            style={{ marginBottom: 12 }}
          />

          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#374151',
              marginBottom: 6,
            }}
          >
            No events yet
          </div>

          <div
            style={{
              fontSize: 13,
              color: '#9CA3AF',
            }}
          >
            Create your first event to engage your students
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fill,minmax(300px,1fr))',
            gap: 16,
          }}
        >
          {events.map(ev => (
            <div
              key={ev.id}
              style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  background:
                    'linear-gradient(135deg,#10B981,#059669)',
                  padding: '16px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 26 }}>
                    {ev.avatar_emoji}
                  </span>

                  <div>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      {ev.category}
                    </div>

                    <div
                      style={{
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 800,
                      }}
                    >
                      {ev.title}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() =>
                    deleteEvent(ev.id, ev.app_event_id)
                  }
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 8px',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={14} color="#fff" />
                </button>
              </div>

              <div style={{ padding: '14px 18px' }}>
                {ev.description && (
                  <p
                    style={{
                      fontSize: 13,
                      color: '#6B7280',
                      margin: '0 0 10px',
                      lineHeight: 1.5,
                    }}
                  >
                    {ev.description}
                  </p>
                )}

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    fontSize: 12,
                    color: '#6B7280',
                  }}
                >
                  <span>
                    📅{' '}
                    {new Date(ev.event_date).toLocaleDateString(
                      'en-US',
                      {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    )}
                  </span>

                  {ev.venue && <span>📍 {ev.venue}</span>}

                  {ev.organizer && (
                    <span>👤 {ev.organizer}</span>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: priorityColor(ev.priority),
                      background: `${priorityColor(
                        ev.priority
                      )}15`,
                      padding: '2px 8px',
                      borderRadius: 4,
                      textTransform: 'uppercase',
                    }}
                  >
                    {ev.priority}
                  </span>

                  {ev.target_degree &&
                    ev.target_degree !== 'all' && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#4F46E5',
                          background: '#EEF2FF',
                          padding: '2px 8px',
                          borderRadius: 4,
                        }}
                      >
                        {ev.target_degree} Students
                      </span>
                    )}

                  {ev.synced_to_app && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#10B981',
                        background: '#F0FDF4',
                        padding: '2px 8px',
                        borderRadius: 4,
                      }}
                    >
                      ✓ On App
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 20,
              width: '100%',
              maxWidth: 540,
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                background: '#fff',
                zIndex: 1,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontWeight: 800,
                  color: '#111827',
                }}
              >
                Create Event
              </h3>

              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <X size={20} color="#6B7280" />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#374151',
                    display: 'block',
                    marginBottom: 5,
                  }}
                >
                  Event Title *
                </label>

                <input
                  value={form.title}
                  onChange={e =>
                    setForm({
                      ...form,
                      title: e.target.value,
                    })
                  }
                  placeholder="e.g. Annual Sports Gala 2025"
                  style={inp}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#374151',
                    display: 'block',
                    marginBottom: 5,
                  }}
                >
                  Description
                </label>

                <textarea
                  value={form.description}
                  onChange={e =>
                    setForm({
                      ...form,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe the event..."
                  rows={3}
                  style={{
                    ...inp,
                    resize: 'vertical',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 14,
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#374151',
                      display: 'block',
                      marginBottom: 5,
                    }}
                  >
                    Category
                  </label>

                  <select
                    value={form.category}
                    onChange={e =>
                      setForm({
                        ...form,
                        category: e.target.value,
                      })
                    }
                    style={inp}
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>
                        {c.charAt(0).toUpperCase() +
                          c.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#374151',
                      display: 'block',
                      marginBottom: 5,
                    }}
                  >
                    Priority
                  </label>

                  <select
                    value={form.priority}
                    onChange={e =>
                      setForm({
                        ...form,
                        priority: e.target.value,
                      })
                    }
                    style={inp}
                  >
                    <option value="normal">Normal</option>
                    <option value="important">
                      Important
                    </option>
                    <option value="very_important">
                      Very Important
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#374151',
                      display: 'block',
                      marginBottom: 5,
                    }}
                  >
                    Date & Time *
                  </label>

                  <input
                    type="datetime-local"
                    value={form.event_date}
                    onChange={e =>
                      setForm({
                        ...form,
                        event_date: e.target.value,
                      })
                    }
                    style={inp}
                  />
                </div>

                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#374151',
                      display: 'block',
                      marginBottom: 5,
                    }}
                  >
                    Venue
                  </label>

                  <input
                    value={form.venue}
                    onChange={e =>
                      setForm({
                        ...form,
                        venue: e.target.value,
                      })
                    }
                    placeholder="e.g. Main Auditorium"
                    style={inp}
                  />
                </div>

                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#374151',
                      display: 'block',
                      marginBottom: 5,
                    }}
                  >
                    Organizer
                  </label>

                  <input
                    value={form.organizer}
                    onChange={e =>
                      setForm({
                        ...form,
                        organizer: e.target.value,
                      })
                    }
                    placeholder={institution?.name}
                    style={inp}
                  />
                </div>

                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#374151',
                      display: 'block',
                      marginBottom: 5,
                    }}
                  >
                    Target Students
                  </label>

                  <select
                    value={form.target_degree}
                    onChange={e =>
                      setForm({
                        ...form,
                        target_degree: e.target.value,
                      })
                    }
                    style={inp}
                  >
                    <option value="all">
                      All Students
                    </option>
                    <option value="BS">BS Students</option>
                    <option value="MS">MS Students</option>
                    <option value="BE">BE Students</option>
                    <option value="MBA">
                      MBA Students
                    </option>
                  </select>
                </div>
              </div>

              <div
                style={{
                  marginTop: 14,
                  marginBottom: 20,
                }}
              >
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#374151',
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  Event Emoji
                </label>

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  {EMOJIS.map(e => (
                    <button
                      key={e}
                      onClick={() =>
                        setForm({
                          ...form,
                          avatar_emoji: e,
                        })
                      }
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        border:
                          form.avatar_emoji === e
                            ? '2.5px solid #4F8EF7'
                            : '1.5px solid #E5E7EB',
                        background:
                          form.avatar_emoji === e
                            ? '#EFF6FF'
                            : '#fff',
                        fontSize: 18,
                        cursor: 'pointer',
                      }}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              <div
                style={{
                  background: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginBottom: 16,
                  fontSize: 12,
                  color: '#92400E',
                }}
              >
                This event will appear on the{' '}
                <strong>student app Events screen</strong>{' '}
                immediately after posting and will remain
                visible regardless of whether its date is today,
                upcoming, or in the past.
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setShowForm(false)}
                  style={{
                    flex: 1,
                    padding: '11px 0',
                    borderRadius: 10,
                    border: '1.5px solid #E5E7EB',
                    background: '#fff',
                    color: '#374151',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={save}
                  disabled={
                    saving ||
                    !form.title.trim() ||
                    !form.event_date
                  }
                  style={{
                    flex: 2,
                    padding: '11px 0',
                    borderRadius: 10,
                    border: 'none',
                    background:
                      'linear-gradient(135deg,#10B981,#059669)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    opacity: saving ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <Send size={14} />
                  {saving
                    ? 'Creating...'
                    : 'Create & Sync to App'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );
}